import React from 'react';
import {
  Box,
  Container,
  Typography,
  Chip,
  Card,
  CardContent,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Button,
} from '@mui/material';
import {
  Security,
  Gavel,
  Person,
  Storage,
  SwapHoriz,
  Delete,
  CheckCircle,
} from '@mui/icons-material';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import ContactFormDialog from '../components/ContactFormDialog';

const rights = [
  {
    icon: <Person />,
    title: 'Right to Access',
    description: 'You can request a copy of all personal data we hold about you.',
  },
  {
    icon: <SwapHoriz />,
    title: 'Right to Rectification',
    description: 'You can request correction of inaccurate or incomplete data.',
  },
  {
    icon: <Delete />,
    title: 'Right to Erasure',
    description: 'You can request deletion of your personal data ("right to be forgotten").',
  },
  {
    icon: <Storage />,
    title: 'Right to Portability',
    description: 'You can request your data in a machine-readable format.',
  },
  {
    icon: <Gavel />,
    title: 'Right to Object',
    description: 'You can object to processing of your data for certain purposes.',
  },
  {
    icon: <Security />,
    title: 'Right to Restrict',
    description: 'You can request restriction of processing in certain circumstances.',
  },
];

const GDPRPage: React.FC = () => {
  const [contactOpen, setContactOpen] = React.useState(false);
  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh' }}>
      <Navbar />

      <Box sx={{ pt: 15, pb: 10 }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Chip label="Legal" color="secondary" sx={{ mb: 2 }} />
            <Typography variant="h2" fontWeight="bold" gutterBottom>
              GDPR Compliance
            </Typography>
            <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 700, mx: 'auto' }}>
              Learn how CleoLead Corp. complies with the General Data Protection Regulation
              and protects the privacy of EU residents.
            </Typography>
          </Box>

          {/* Overview Card */}
          <Card sx={{ p: 4, mb: 6, bgcolor: 'primary.dark', color: 'white' }}>
            <Grid container spacing={4} alignItems="center">
              <Grid size={{ xs: 12, md: 8 }}>
                <Typography variant="h4" fontWeight="bold" gutterBottom>
                  Our Commitment to GDPR
                </Typography>
                <Typography variant="body1" sx={{ opacity: 0.9 }}>
                  CleoLead Corp. is fully committed to GDPR compliance. We have implemented comprehensive
                  measures to protect the personal data of our users and their customers in the European Union.
                  This includes data protection by design, transparent processing, and respect for data subject rights.
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <Box sx={{ textAlign: 'center' }}>
                  <Security sx={{ fontSize: 80, opacity: 0.8 }} />
                </Box>
              </Grid>
            </Grid>
          </Card>

          {/* Your Rights */}
          <Typography variant="h4" fontWeight="bold" gutterBottom sx={{ mt: 6, mb: 4 }}>
            Your Rights Under GDPR
          </Typography>
          <Grid container spacing={3}>
            {rights.map((right, index) => (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={index}>
                <Card sx={{ height: '100%' }}>
                  <CardContent>
                    <Box sx={{ color: 'primary.main', mb: 2 }}>
                      {right.icon}
                    </Box>
                    <Typography variant="h6" fontWeight="bold" gutterBottom>
                      {right.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {right.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          {/* Data Processing */}
          <Typography variant="h4" fontWeight="bold" gutterBottom sx={{ mt: 8 }}>
            How We Process Your Data
          </Typography>
          <Card sx={{ p: 4, mb: 4 }}>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              Legal Basis for Processing
            </Typography>
            <Typography variant="body1" paragraph>
              We process personal data under the following legal bases as defined by GDPR Article 6:
            </Typography>
            <List>
              {[
                'Contract Performance: Processing necessary to provide our services to you',
                'Legitimate Interests: Analytics, security, and service improvement',
                'Consent: Marketing communications and optional features',
                'Legal Obligation: Compliance with applicable laws and regulations',
              ].map((item, index) => (
                <ListItem key={index} sx={{ py: 0.5 }}>
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <CheckCircle color="primary" fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary={item} />
                </ListItem>
              ))}
            </List>
          </Card>

          {/* Data Controller vs Processor */}
          <Typography variant="h4" fontWeight="bold" gutterBottom sx={{ mt: 6 }}>
            Data Controller vs. Data Processor
          </Typography>
          <Grid container spacing={4}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Card sx={{ height: '100%', p: 3 }}>
                <Typography variant="h6" fontWeight="bold" gutterBottom color="primary">
                  CleoLead as Data Controller
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  We act as the data controller for your account information, payment data, and usage analytics.
                  We determine the purposes and means of processing this data.
                </Typography>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Card sx={{ height: '100%', p: 3 }}>
                <Typography variant="h6" fontWeight="bold" gutterBottom color="secondary">
                  CleoLead as Data Processor
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  We act as a data processor for the conversations and customer data you collect through our platform.
                  You remain the data controller for your customers' data.
                </Typography>
              </Card>
            </Grid>
          </Grid>

          {/* Data Protection Measures */}
          <Typography variant="h4" fontWeight="bold" gutterBottom sx={{ mt: 8 }}>
            Data Protection Measures
          </Typography>
          <Card sx={{ p: 4, mb: 4 }}>
            <Grid container spacing={3}>
              {[
                { title: 'Encryption', description: 'All data encrypted in transit (TLS 1.3) and at rest (AES-256)' },
                { title: 'Access Controls', description: 'Role-based access with multi-factor authentication' },
                { title: 'Data Minimization', description: 'We only collect data necessary for our services' },
                { title: 'Regular Audits', description: 'Annual third-party security assessments' },
                { title: 'Incident Response', description: '72-hour breach notification as required by GDPR' },
                { title: 'DPO Appointed', description: 'Dedicated Data Protection Officer for compliance' },
              ].map((item, index) => (
                <Grid size={{ xs: 12, sm: 6, md: 4 }} key={index}>
                  <Box>
                    <Typography variant="subtitle1" fontWeight="bold">
                      {item.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {item.description}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Card>

          {/* International Transfers */}
          <Typography variant="h4" fontWeight="bold" gutterBottom sx={{ mt: 6 }}>
            International Data Transfers
          </Typography>
          <Typography variant="body1" paragraph>
            When we transfer personal data outside the European Economic Area (EEA), we ensure adequate
            protection through:
          </Typography>
          <List>
            {[
              'Standard Contractual Clauses (SCCs) approved by the European Commission',
              'Data processing agreements with all sub-processors',
              'Regular assessment of transfer mechanisms',
              'EU-based data hosting options for Enterprise customers',
            ].map((item, index) => (
              <ListItem key={index} sx={{ py: 0.5 }}>
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <CheckCircle color="primary" fontSize="small" />
                </ListItemIcon>
                <ListItemText primary={item} />
              </ListItem>
            ))}
          </List>

          {/* Exercise Your Rights */}
          <Card sx={{ p: 4, mt: 6, bgcolor: 'background.paper' }}>
            <Typography variant="h5" fontWeight="bold" gutterBottom>
              Exercise Your Rights
            </Typography>
            <Typography variant="body1" paragraph>
              To exercise any of your GDPR rights, you can:
            </Typography>
            <List>
              <ListItem sx={{ py: 0.5 }}>
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <CheckCircle color="primary" fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Use the privacy controls in your account settings" />
              </ListItem>
              <ListItem sx={{ py: 0.5 }}>
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <CheckCircle color="primary" fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Email our Data Protection Officer at support@cleolead.com" />
              </ListItem>
              <ListItem sx={{ py: 0.5 }}>
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <CheckCircle color="primary" fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Submit a formal request through our privacy portal" />
              </ListItem>
            </List>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              We will respond to your request within 30 days as required by GDPR.
            </Typography>
            <Button variant="contained" sx={{ mt: 3 }} onClick={() => setContactOpen(true)}>
              Submit Privacy Request
            </Button>
          </Card>

          {/* Contact */}
          <Typography variant="h4" fontWeight="bold" gutterBottom sx={{ mt: 8 }}>
            Contact Our DPO
          </Typography>
          <Card sx={{ p: 3, bgcolor: 'background.paper' }}>
            <Typography variant="body1">
              <strong>Data Protection Officer</strong><br />
              Email: <span onClick={() => setContactOpen(true)} style={{ color: '#C75B39', cursor: 'pointer', textDecoration: 'underline' }} role="button" tabIndex={0} onKeyDown={(e) => { if (e.key === 'Enter') setContactOpen(true); }}>support@cleolead.com</span>
            </Typography>
          </Card>
        </Container>
      </Box>

      <Footer />
      <ContactFormDialog
        open={contactOpen}
        onClose={() => setContactOpen(false)}
        title="Privacy Request"
        subtitle="Submit your GDPR privacy request. We'll respond within 30 days as required by regulation."
        formType="privacy"
        defaultSubject="Privacy Request - GDPR"
      />
    </Box>
  );
};

export default GDPRPage;
